import React, {useEffect, useState} from 'react';
import { Button, Image, Form, Input, Alert } from 'antd';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import { getCsrfToken } from "@/components/common/utils/utils";
import { useTheme } from "@/components/common/ThemeProvider";
import { useCompany } from "@/hooks/company/use-company";
import {useCanSelfRegister} from "@/modules/onboarding/hooks/useSelfOnboardingConfig";
import { useGetLoginLogo } from "@/shared/api/client/appearance-controller";
import { useGetVersion } from "@/shared/api/client/version-controller";

const LoginFormView = () => {
    const navigate = useNavigate();
    const { dark, toggleTheme } = useTheme();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isLoading, isError, data, error } = useCompany();
    const [searchParams] = useSearchParams();
    const [{isLoading: canSelfRegisterLoading, data: canSelfRegister}] = useCanSelfRegister();
    const { data: logoData } = useGetLoginLogo();
    const logoSrc = typeof logoData === 'string' ? logoData : logoData?.data || '/images/hazina-logo.jpg';
    const { data: versionData } = useGetVersion();
    
    // Get error message from URL params
    const errorParam = searchParams.get('error');
    const errorMessage = searchParams.get('message') || 'Invalid username or password';

    const onFinish = (values: any) => {
        setIsSubmitting(true);
        
        // Handle form submission programmatically
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/api/auth/login';
        
        // Add form fields
        Object.keys(values).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = values[key];
            form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
    };

    useEffect(() => {
        const action = searchParams.get('a');
        if (action === 'self-register') {
            navigate('/register'); // Replace with your target route
        }
        if (action === 'self-invitation') {
            navigate('/self-invitation'); // Replace with your target route
        }
    }, [searchParams, navigate]);

    if (isLoading || canSelfRegisterLoading) {
        return <div>Loading...</div>;
    }

    if (isError) {
        console.error("Error in useCompany:", error);
        return <div>Something went wrong </div>;
    }

    if (!data) {
        return <div>Something went wrong </div>;
    }

    const { brFname, brsesDate } = data;

    return (
        <div className={`min-h-screen flex items-center justify-center ${dark ? 'bg-gray-900' : 'bg-slate-700 bg-opacity-90'}`}>
            <div className={`max-w-md w-full space-y-8 p-10 ${dark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl`}>
                <div className="flex justify-center">
                    <Image height={200} src={logoSrc} alt="Company Logo" preview={false} />
                </div>
                <h2 className={`mt-6 text-center text-2xl font-bold ${dark ? 'text-white' : 'text-gray-800'}`}>
                    Sign In to {brFname}
                </h2>
                
                {/* Display error message if present */}
                {errorParam && (
                    <Alert
                        message="Login Failed"
                        description={decodeURIComponent(errorMessage)}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                    />
                )}
                
                <Form
                    name="loginForm"
                    onFinish={onFinish}
                    layout="vertical"
                    size="middle"
                    component="form"
                >
                    <Form.Item
                        label="Username"
                        name="username"
                        rules={[{ required: true, message: 'Please input your username!' }]}
                    >
                        <Input
                            placeholder="Enter your username"
                            disabled={isSubmitting}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[{ required: true, message: 'Please input your password!' }]}
                    >
                        <Input.Password
                            placeholder="Enter your password"
                            disabled={isSubmitting}
                        />
                    </Form.Item>

                    <div className="flex items-center justify-between mb-4">
                        <div className="text-sm">
                            <Link to="/forgot-password"
                                  className={`font-medium ${dark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}>
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isSubmitting}
                            className="w-full"
                        >
                            {isSubmitting ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </Form.Item>
                </Form>
                <div className="mt-6 text-center text-sm">
                    <p className={dark ? 'text-gray-400' : 'text-gray-600'}>
                        By signing in I acknowledge that I have read and understood the
                        <br/>
                        terms and conditions when using Saccoss Portal
                    </p>

                    {canSelfRegister && (
                        <>
                            <p className="mt-4">
                                <Link
                                    to={"/register"}
                                    className={`font-medium ${dark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
                                >
                                    New member? Join Here
                                </Link>
                            </p>
                        </>
                    )}
                </div>
                <div className="mt-4 text-center" style={{ fontSize: 11, opacity: 0.5 }}>
                    <span>v{versionData?.version || 'dev-local'} &mdash; Powered by </span>
                    <a href={versionData?.website || 'https://scalefi.co.tz'} target="_blank" rel="noopener noreferrer"
                       className={dark ? 'text-gray-400' : 'text-gray-500'}
                       style={{ textDecoration: 'underline' }}>
                        {versionData?.poweredBy || 'ScaleFi'}
                    </a>
                </div>
            </div>
        </div>
    );
};

export default LoginFormView;